import type { Event } from '@cocoonspace/sdk-js/types/event'
import { ArrowRight } from 'lucide-react'
import { Trans, useTranslation } from 'next-i18next'
import Link from 'next/link'
import { type PropsWithChildren, useState } from 'react'
import { buttonVariants } from '~/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '~/components/ui/dialog'
import { useTracking } from '~/providers/tracking-provider'
import { EventShareWidget } from '../event-share-widget'

export const EventShareDialog = ({
	children,
	event,
}: PropsWithChildren<{
	event: Event
}>) => {
	const { t } = useTranslation()
	const [open, setOpen] = useState(false)
	const { track } = useTracking()

	const closeDialog = () => setOpen(false)

	const handleOnInvitationsSent = (guestCount: number) => {
		closeDialog()
		track.eventInvitation({ source: 'shareDialog', guestCount })
	}

	return (
		<Dialog
			open={open}
			onOpenChange={setOpen}
		>
			<DialogTrigger asChild>{children}</DialogTrigger>

			<DialogContent className='mx-auto flex w-full max-w-[800px] flex-col gap-y-4'>
				<DialogHeader>
					<DialogTitle>{t('booking:share.label')}</DialogTitle>
				</DialogHeader>

				<div className='flex flex-col items-start justify-start gap-y-2'>
					<Trans
						i18nKey='booking:share.body'
						parent='p'
						components={{
							strong: <strong />,
							br: <br className='hidden md:ml-0 md:block' />,
						}}
					/>

					<Link
						href={{
							pathname: `/event/${event.id}/edit`,
							query: { autofocus: 'invitation' },
							hash: '#event-guest-list',
						}}
						className={buttonVariants({ variant: 'textPrimary' })}
					>
						{t('cwEvents:share.seeGuestList')}

						<ArrowRight
							size={16}
							className='ml-1'
						/>
					</Link>
				</div>

				<EventShareWidget
					event={event}
					onInvitationsSent={handleOnInvitationsSent}
				/>
			</DialogContent>
		</Dialog>
	)
}
